import React, { useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { SERVER_BASE_URL } from "../config"; // Импорт централизованного базового URL

function EditPermanentInvitationModal({ invitation, isOpen, onClose, onUpdated }) {
  const [name, setName] = useState(invitation.invitee_name || "");
  const [email, setEmail] = useState(invitation.invitee_email || "");
  const [inviteeCategory, setInviteeCategory] = useState(invitation.guest_category || "REGULAR"); // Категория для самого приглашаемого
  
  // Инициализируем entries на основе invitation.variants, убедившись, что это массив
  const [entries, setEntries] = useState(
    Array.isArray(invitation.variants)
      ? invitation.variants.map((variant) => ({
          type: variant.type.replace(/%$/, ''), // Removes % if it exists
          count: variant.limit,
          category: variant.category || "REGULAR" // Категория для каждого варианта
        }))
      : []
  );

  // Функция форматирования типа билета
  const formatTicketType = (type) => {
    if (!type) return "";
    
    // Если тип содержит только цифры, добавляем знак процента
    if (/^\d+$/.test(type)) {
      return `${type}%`;
    }
    
    return type;
  };

  // Обработка изменений в полях вариантов
  const handleChange = (index, field, value) => {
    const updatedEntries = [...entries];
    if (field === "type") {
      // Удаляем знак %, если пользователь ввел его вручную
      const cleanValue = value.replace(/%$/, '');
      updatedEntries[index].type = cleanValue;
    } else if (field === "count") {
      // Улучшенная обработка числового ввода
      const newCount = value === "" ? 0 : Number(value);
      // Убеждаемся, что значение неотрицательное
      updatedEntries[index].count = Math.max(0, newCount);
    } else if (field === "category") {
      updatedEntries[index].category = value;
    }
    setEntries(updatedEntries);
  };

  const addEntry = () => {
    setEntries([...entries, { type: "", count: 0, category: "REGULAR" }]);
  };

  const removeEntry = (index) => {
    const updatedEntries = entries.filter((_, i) => i !== index);
    setEntries(updatedEntries);
  };

  /**
   * Нажатие на "Save"
   */
  const handleSave = async () => {
    try {
      // Формируем тело запроса под схему обновления PermanentInvitation
      const updatedData = {
        invitee_name: name,
        invitee_email: email,
        guest_category: inviteeCategory, // Категория для самого приглашаемого
        variants: entries.map((e) => ({
          type: formatTicketType(e.type),
          limit: e.count,
          category: e.category // Категория для каждого варианта
        })),
      };

      const token = localStorage.getItem("token");
      const response = await fetch(
        `${SERVER_BASE_URL}/api/permanent-invitations/invitations/${invitation.id}`,
        {
          method: "PUT", // или PATCH в зависимости от API, проверьте документацию backend
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedData),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update permanent invitation");
      }

      const data = await response.json();
      console.log("Updated permanent invitation:", data);
      toast.success("Permanent invitation successfully updated.");

      // Передаём обновлённые данные родительскому компоненту для обновления списка
      if (onUpdated) {
        onUpdated(data);
      }

      onClose();
    } catch (error) {
      console.error("Error updating permanent invitation:", error);
      toast.error("Failed to update permanent invitation.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white p-4 rounded-lg shadow-lg w-[400px] relative">
        <h2 className="text-lg font-bold mb-3 text-gray-800">Edit Permanent Invitation</h2>

        <div className="mb-3">
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Name
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 p-1.5 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              placeholder="Enter name, e.g. John Doe"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Email
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 p-1.5 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              placeholder="Enter email"
            />
          </div>
        </div>

        {/* Категория для самого приглашаемого */}
        <div className="mb-3">
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Guest Category
          </label>
          <select
            value={inviteeCategory}
            onChange={(e) => setInviteeCategory(e.target.value)}
            className="w-full border border-gray-300 p-1.5 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          >
            <option value="REGULAR">Regular</option>
            <option value="VIP">VIP</option>
            <option value="BACKSTAGE">Backstage</option>
            <option value="ARTIST">Artist</option>
            <option value="STAFF">Staff</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            This category will be assigned to the invitee when they are added to the guest list
          </p>
        </div>

        <div className="mb-4">
          <label className="block mb-1.5 text-sm font-medium text-gray-700">
            Admission Details
          </label>
          
          {/* Заголовки колонок */}
          <div className="grid grid-cols-[1fr,110px,80px,40px] gap-2 mb-1 text-xs text-gray-600 font-medium">
            <div>Type</div>
            <div>Category</div>
            <div>Count</div>
            <div></div>
          </div>
          
          <div className="space-y-2">
            {entries.map((entry, index) => (
              <div
                key={index}
                className="grid grid-cols-[1fr,110px,80px,40px] gap-2 items-center"
              >
                <div className="relative">
                  <input
                    type="text"
                    value={entry.type}
                    onChange={(e) => handleChange(index, "type", e.target.value)}
                    className="w-full border border-gray-300 p-1.5 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="Free, 50, ..."
                    onFocus={(e) => e.target.select()}
                  />
                  {/^\d+$/.test(entry.type) && (
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                  )}
                </div>
                
                {/* Селектор категории для каждой строки */}
                <select
                  value={entry.category}
                  onChange={(e) => handleChange(index, "category", e.target.value)}
                  className="border border-gray-300 p-1.5 rounded-md focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                >
                  <option value="REGULAR">Regular</option>
                  <option value="VIP">VIP</option>
                  <option value="BACKSTAGE">Backstage</option>
                  <option value="ARTIST">Artist</option>
                  <option value="STAFF">Staff</option>
                </select>
                
                <input
                  type="number"
                  min={0}
                  value={entry.count}
                  onChange={(e) => handleChange(index, "count", e.target.value)}
                  onFocus={(e) => e.target.select()}
                  className="w-full border border-gray-300 p-1.5 rounded-md text-center focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  placeholder="Count"
                />
                <button
                  onClick={() => removeEntry(index)}
                  className="text-red-500 hover:text-red-700 transition duration-200 mx-auto"
                  aria-label="Remove entry"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={addEntry}
            className="mt-2 bg-indigo-600 text-white px-3 py-1 rounded text-sm hover:bg-indigo-700 transition duration-200 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Row
          </button>
        </div>

        <div className="flex justify-center space-x-4">
          <button
            onClick={handleSave}
            disabled={!name.trim() || !email.trim()}
            className="px-5 py-1.5 bg-indigo-600 text-white font-medium rounded hover:bg-indigo-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Save
          </button>
          <button
            onClick={onClose}
            className="px-5 py-1.5 bg-white text-gray-700 font-medium rounded hover:bg-gray-100 border border-gray-300 transition duration-200 text-sm"
          >
            Cancel
          </button>
        </div>

        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 transition duration-200"
          aria-label="Close"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

EditPermanentInvitationModal.propTypes = {
  invitation: PropTypes.shape({
    id: PropTypes.number.isRequired, // Ensure invitation has an ID
    invitee_name: PropTypes.string,
    invitee_email: PropTypes.string,
    guest_category: PropTypes.string,
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        limit: PropTypes.number.isRequired,
        category: PropTypes.string,
      })
    ),
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func,
};

export default EditPermanentInvitationModal;